import { ethers, BigNumber } from "ethers";
import {
  DexConfig,
  DexServiceConfig,
  SwapParams,
  LogLevel,
  LogData,
  WalletConfig,
  TokenInfo,
  SwapEstimation,
  SwapResult,
} from "../types/dex";

/**
 * Four.meme路由合约的ABI
 * 包含了代币兑换所需的基本方法：
 * - buyToken: 使用BNB购买代币
 * - buyTokenAMAP: 使用BNB购买代币（自动计算最大购买金额）
 * - sellToken: 使用代币兑换BNB
 */
import { FOUR_MEME_ABI } from "./four.meme.abi";

/**
 * DEX路由合约的通用ABI
 * 包含了代币兑换所需的基本方法：
 * - getAmountsOut: 计算兑换后能获得的代币数量
 * - swapExactTokensForTokens: 使用精确的输入代币数量换取输出代币
 */
const ROUTER_ABI = [
  "function getAmountsOut(uint amountIn, address[] memory path) public view returns (uint[] memory amounts)",
  "function swapExactTokensForTokens(uint amountIn, uint amountOutMin, address[] calldata path, address to, uint deadline) external returns (uint[] memory amounts)",
  "function swapExactTokensForTokensSupportingFeeOnTransferTokens(uint amountIn, uint amountOutMin, address[] calldata path, address to, uint deadline) external",
  "function swapExactETHForTokensSupportingFeeOnTransferTokens(uint256 amountOutMin, address[] calldata path, address to, uint256 deadline) external payable",
  "function swapExactTokensForETHSupportingFeeOnTransferTokens(uint256 amountIn, uint256 amountOutMin, address[] calldata path, address to, uint256 deadline) external",
];

const NATIVE_TOKEN = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee";
const WBNB = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
const FOUR_MEME_TOKEN = "0x5c952063c7fc8610ffdb798152d69f0b9550762b";
/**
 * ERC20代币合约的通用ABI
 * 包含了代币交互所需的基本方法：
 * - name: 获取代币名称
 * - symbol: 获取代币符号
 * - decimals: 获取代币精度
 * - totalSupply: 获取代币总供应量
 * - approve: 授权DEX路由合约使用代币
 * - allowance: 查询授权额度
 * - balanceOf: 查询代币余额
 */
const ERC20_ABI = [
  "function name() public view returns (string)",
  "function symbol() public view returns (string)",
  "function decimals() public view returns (uint8)",
  "function totalSupply() public view returns (uint256)",
  "function approve(address spender, uint256 amount) public returns (bool)",
  "function allowance(address owner, address spender) public view returns (uint256)",
  "function balanceOf(address account) public view returns (uint256)",
];

/**
 * DEX服务类
 * 提供与去中心化交易所交互的功能，包括：
 * - 代币兑换
 * - 兑换金额估算
 * - 代币授权
 * - 余额查询等
 */
export class DexService {
  private config: DexServiceConfig;
  private provider: ethers.providers.JsonRpcProvider;
  private wallets: ethers.Wallet[] = [];
  private router: ethers.Contract;
  private factory: ethers.Contract;
  //线程数量
  private maxThreads: number;
  createAt: number;
  runningEnv: "worker" | "browser";

  /**
   * 发送日志到UI线程
   */
  protected log(
    level: LogLevel,
    message: string,
    walletIndex?: number | null,
    data?: any
  ) {
    if (this.config.onLog) {
      const logData: LogData = {
        level,
        message,
        timestamp: Date.now(),
        walletIndex,
        data,
        state: JSON.parse(JSON.stringify(this)),
      };
      this.config.onLog(logData);
    }
  }

  /**
   * 创建DexService实例
   * @param config DexService配置
   */
  constructor(config: DexServiceConfig) {
    console.log("DexService配置:", config);


    this.maxThreads = config.maxThreads || 10;
    this.config = config;
    this.provider = new ethers.providers.JsonRpcProvider(config.rpc.url);
    this.router = new ethers.Contract(
      config.dex.router.address,
      ROUTER_ABI,
      this.provider
    );
    this.factory = new ethers.Contract(
      config.dex.factory.address,
      [
        "function getPair(address tokenA, address tokenB) external view returns (address pair)",
      ],
      this.provider
    );
    this.createAt = new Date().getTime();
    // 判断运行环境
    /* eslint-disable no-restricted-globals */
    this.runningEnv =
      typeof window === "undefined" && typeof self !== "undefined"
        ? "worker"
        : "browser";
    /* eslint-enable no-restricted-globals */
  }

  /**
   * 初始化钱包
   * @param wallets 钱包配置列表
   */
  public async initialize(wallets: WalletConfig[]): Promise<void> {
    try {
      this.log(LogLevel.INFO, `开始初始化DexService`, null, {
        rpc: this.config.rpc.name,
        router: this.config.dex.router.name,
        walletCount: wallets.length,
      });

      // 验证RPC连接
      const network = await this.provider.getNetwork();
      if (network.chainId !== this.config.rpc.chainId) {
        throw new Error(
          `RPC链ID不匹配: 期望 ${this.config.rpc.chainId}, 实际 ${network.chainId}`
        );
      }
      this.log(LogLevel.SUCCESS, `RPC连接成功: ${this.config.rpc.name}`);

      console.log("验证路由合约this.config", this.config);
      console.log("验证路由合约", this.config.dex.router.address);
      // 验证路由合约
      const routerCode = await this.provider.getCode(
        this.config.dex.router.address
      );
      if (routerCode === "0x") {
        throw new Error(`路由合约地址无效: ${this.config.dex.router.address}`);
      }
      this.log(
        LogLevel.SUCCESS,
        `路由合约验证成功: ${this.config.dex.router.name}`
      );

      // 初始化钱包
      if (wallets && wallets.length > 0) {
        const privateKeys = wallets
          .filter((w) => w && w.privateKey)
          .map((w) => w.privateKey);
        console.log("调用初始化钱包 - 获得的有效私钥数量", privateKeys.length);
        if (privateKeys.length > 0) {
          await this.updateWallets(privateKeys);
        }
      }

      this.log(LogLevel.SUCCESS, "DexService初始化完成", null, {
        walletCount: this.wallets.length,
      });
    } catch (error: any) {
      this.log(LogLevel.ERROR, `DexService初始化失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 获取所有钱包地址
   */
  public getWalletAddresses(): string[] {
    return this.wallets.map((wallet) => wallet.address);
  }

  /**
   * 获取指定钱包的状态
   * @param walletIndex 钱包索引
   */
  public async getWalletStatus(walletIndex: number) {
    try {
      const wallet = this.wallets[walletIndex];
      if (!wallet) {
        throw new Error(`钱包索引无效: ${walletIndex}`);
      }

      const balance = await this.provider.getBalance(wallet.address);
      this.log(LogLevel.INFO, `获取钱包状态`, walletIndex, {
        address: wallet.address,
        balance: ethers.utils.formatEther(balance),
      });

      return {
        address: wallet.address,
        balance: ethers.utils.formatEther(balance),
      };
    } catch (error: any) {
      this.log(
        LogLevel.ERROR,
        `获取钱包状态失败: ${error.message}`,
        walletIndex,
        { error }
      );
      throw error;
    }
  }

  /**
   * 获取所有钱包的状态
   */
  public async getAllWalletStatus() {
    const statuses = [];
    for (let i = 0; i < this.wallets.length; i++) {
      try {
        const status = await this.getWalletStatus(i);
        statuses.push({ index: i, ...status, error: null });
      } catch (error: any) {
        statuses.push({
          index: i,
          address: this.wallets[i].address,
          balance: null,
          error: error.message,
        });
      }
    }
    return statuses;
  }

  /**
   * 添加或更新钱包
   * @param privateKeys 私钥列表
   */
  public updateWallets(privateKeys: string[]) {
    try {
      this.log(LogLevel.INFO, `开始更新钱包列表，数量: ${privateKeys.length}`);
      // 验证私钥格式
      for (const key of privateKeys) {
        if (
          !ethers.utils.isHexString(key, 32) &&
          !key.match(/^[0-9a-fA-F]{64}$/)
        ) {
          this.log(LogLevel.ERROR, "无效的私钥格式");
          throw new Error("无效的私钥格式");
        }
      }
      // // 清空现有钱包
      // this.wallets = [];

      // 合并钱包
      const newWalletsMap = new Map<string, ethers.Wallet>();
      for (const key of privateKeys) {
        const wallet = new ethers.Wallet(key, this.provider);
        newWalletsMap.set(wallet.address, wallet);
      }

      for (const wallet of this.wallets) {
        newWalletsMap.set(wallet.address, wallet);
      }

      // 添加新钱包
      this.wallets = Array.from(newWalletsMap.values()).reduce(
        (acc, wallet) => {
          if (!acc.find((w) => w.address === wallet.address)) {
            acc.push(wallet);
          }
          return acc;
        },
        this.wallets
      );

      console.log("更新钱包列表，当前钱包数量:", this.wallets.length);

      this.log(
        LogLevel.SUCCESS,
        `成功更新钱包列表，当前钱包数量: ${this.wallets.length}`
      );
    } catch (error: Error | any) {
      this.log(LogLevel.ERROR, `更新钱包失败: ${error.message}`, null, {
        error,
      });
      console.error("更新钱包失败:", error);
      throw error;
    }
  }

  /**
   * 获取当前钱包数量
   */
  public getWalletCount(): number {
    return this.wallets.length;
  }

  /**
   * 检查钱包是否已添加
   */
  public hasWallets(): boolean {
    return this.wallets.length > 0;
  }

  /**
   * 获取代币合约实例
   * @param tokenAddress 代币合约地址
   * @param signer 可选的签名者，用于发送交易
   * @returns 代币合约实例
   * @protected
   */
  protected getTokenContract(tokenAddress: string, signer?: ethers.Signer) {
    return new ethers.Contract(
      tokenAddress,
      ERC20_ABI,
      signer || this.provider
    );
  }

  /**
   * 获取代币信息
   * @param tokenAddress 代币合约地址
   * @returns 代币信息
   */
  public async getTokenInfo(tokenAddress: string): Promise<TokenInfo> {
    try {
      // 如果是原生代币，返回固定信息
      if (tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return {
          name: "Binance Coin",
          symbol: "BNB",
          decimals: 18,
          address: NATIVE_TOKEN,
          totalSupply: ethers.constants.MaxUint256,
        };
      }

      this.log(LogLevel.INFO, "开始获取代币信息", null, { tokenAddress });

      if (!ethers.utils.isAddress(tokenAddress)) {
        throw new Error("无效的代币合约地址");
      }

      const contract = this.getTokenContract(tokenAddress);
      const [name, symbol, decimals, totalSupply] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals(),
        contract.totalSupply(),
      ]);

      const tokenInfo: TokenInfo = {
        address: tokenAddress, // 添加代币地址
        name,
        symbol,
        decimals,
        totalSupply,
      };

      this.log(LogLevel.SUCCESS, "获取代币信息成功", null, tokenInfo);
      return tokenInfo;
    } catch (error: any) {
      this.log(LogLevel.ERROR, `获取代币信息失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  isFourMemeToken(tokenAddress: string): boolean {
    return tokenAddress.toLowerCase() === FOUR_MEME_TOKEN.toLowerCase();
  }

  isNativeToken(tokenAddress: string): boolean {
    return tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase();
  }

  isUsingFourMeme(): boolean {
    const dexRouter = this.config.dex.router.address.toLowerCase();
    return this.isFourMemeToken(dexRouter);
  }

  /**
   * 检查并授权代币使用权限
   * 如果授权额度不足，会发起新的授权交易
   * @param tokenAddress 代币合约地址
   * @param amount 需要授权的金额
   * @param decimals 代币精度
   * @param walletIndex 钱包索引
   * @protected
   */
  protected async checkAndApproveToken(
    tokenAddress: string,
    amount: string | BigNumber,
    decimals: number,
    walletIndex: number = 0,
    params: SwapParams
  ): Promise<void> {
    // 如果是原生代币，不需要授权
    if (this.isNativeToken(tokenAddress)) {
      this.log(LogLevel.INFO, "跳过原生代币授权", walletIndex, {
        token: tokenAddress,
      });
      return;
    }
    const options = await this.buildGasOptions(params);
    // 获取钱包实例
    const wallet = this.wallets[walletIndex];
    // Four.meme 路由处理
    if (this.isUsingFourMeme()) {
      this.log(LogLevel.INFO, "使用 Four.meme 执行兑换", null, {
        params,
        maxBuyAmount: params.maxBuyAmount?.toString(),
      });

      const router = new ethers.Contract(
        this.config.dex.router.address,
        FOUR_MEME_ABI,
        wallet
      );

      const amountIn = ethers.utils.parseUnits(
        params.amountIn,
        params.tokenInDecimals
      );
      const swapParams = {
        ...options,
        value: this.isNativeToken(params.tokenIn) ? amountIn : 0,
      };

      // 返回，不执行approve操作
      // FourMeme处理应该只在handleFourMemeSwap中执行，这里不应该直接进行交易
      this.log(LogLevel.INFO, "FourMeme交易将在handleFourMemeSwap中执行", null, null);
      return;
    }

    const tokenContract = await this.getTokenContract(tokenAddress, wallet);

    // 转换输入金额为BigNumber
    const amountBN =
      typeof amount === "string"
        ? ethers.utils.parseUnits(amount, decimals)
        : amount;

    // 检查当前授权额度
    const allowance = await tokenContract.allowance(
      wallet.address,
      this.config.dex.router.address
    );

    // 如果授权额度不足，发起新的授权
    if (allowance.lt(amountBN)) {
      this.log(LogLevel.INFO, "开始授权代币", walletIndex, {
        token: tokenAddress,
        router: this.config.dex.router.address,
        isFourMeme: this.isUsingFourMeme(),
      });

      const tx = await tokenContract.approve(
        this.config.dex.router.address,
        ethers.constants.MaxUint256,
        options
      );
      await tx.wait();
    }
  }

  /**
   * 获取代币授权额度
   * @param tokenAddress 代币合约地址
   * @param ownerAddress 持有者地址
   * @param spenderAddress 授权地址
   * @returns 授权额度
   */
  public async getAllowance(
    tokenAddress: string,
    ownerAddress: string,
    spenderAddress: string
  ): Promise<BigNumber> {
    try {
      // 如果是原生代币，直接返回最大值
      if (tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return ethers.constants.MaxUint256;
      }

      this.log(LogLevel.INFO, "开始查询代币授权额度", null, {
        token: tokenAddress,
        owner: ownerAddress,
        spender: spenderAddress,
      });

      if (!ethers.utils.isAddress(tokenAddress)) {
        throw new Error("无效的代币合约地址");
      }
      if (!ethers.utils.isAddress(ownerAddress)) {
        throw new Error("无效的持有者地址");
      }
      if (!ethers.utils.isAddress(spenderAddress)) {
        throw new Error("无效的授权地址");
      }

      const tokenContract = this.getTokenContract(tokenAddress);
      const allowance = await tokenContract.allowance(
        ownerAddress,
        spenderAddress
      );

      this.log(LogLevel.SUCCESS, "获取代币授权额度成功", null, {
        token: tokenAddress,
        owner: ownerAddress,
        spender: spenderAddress,
        allowance: ethers.utils.formatUnits(allowance),
      });

      return allowance;
    } catch (error: any) {
      this.log(LogLevel.ERROR, `获取代币授权额度失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 授权代币使用权限
   * @param tokenAddress 代币合约地址
   * @param spenderAddress 授权地址
   * @param amount 授权金额
   * @param ownerAddress 持有者地址
   * @returns 交易结果
   */
  public async approve(
    tokenAddress: string,
    spenderAddress: string,
    amount: BigNumber,
    ownerAddress: string
  ): Promise<ethers.ContractTransaction> {
    try {
      this.log(LogLevel.INFO, "开始授权代币", null, {
        token: tokenAddress,
        spender: spenderAddress,
        amount: amount.toString(),
        owner: ownerAddress,
      });

      // 如果是原生代币，不需要授权
      if (tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return Promise.resolve({
          hash: "0x0000000000000000000000000000000000000000000000000000000000000000",
        } as ethers.ContractTransaction);
      }

      if (!ethers.utils.isAddress(tokenAddress)) {
        throw new Error("无效的代币合约地址");
      }
      if (!ethers.utils.isAddress(spenderAddress)) {
        throw new Error("无效的授权地址");
      }
      if (!ethers.utils.isAddress(ownerAddress)) {
        throw new Error("无效的持有者地址");
      }

      // 查找钱包
      const walletIndex = this.wallets.findIndex(
        (w) => w.address.toLowerCase() === ownerAddress.toLowerCase()
      );
      if (walletIndex === -1) {
        throw new Error("找不到对应的钱包");
      }

      const wallet = this.wallets[walletIndex];
      const tokenContract = this.getTokenContract(tokenAddress, wallet);
      const tx = await tokenContract.approve(spenderAddress, amount);

      this.log(LogLevel.SUCCESS, "代币授权交易已发送", null, {
        token: tokenAddress,
        spender: spenderAddress,
        amount: amount.toString(),
        owner: ownerAddress,
        txHash: tx.hash,
      });

      return tx;
    } catch (error: any) {
      this.log(LogLevel.ERROR, `代币授权失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 验证代币金额是否合理
   * @param amount 金额
   * @param decimals 精度
   * @param symbol 代币符号
   * @returns 如果金额不合理则抛出错误
   */
  protected validateTokenAmount(
    amount: BigNumber,
    decimals: number,
    symbol: string
  ) {
    // 检查金额是否为0
    if (amount.isZero()) {
      throw new Error(`${symbol}金额为0`);
    }
    // 如果是原生代币，检查钱包余额
    if (symbol === "BNB") {
      // 这里可以添加余额检查逻辑
      return;
    }
    // 检查金额是否过大
    const formatted = ethers.utils.formatUnits(amount, decimals);
    const value = parseFloat(formatted);

    // 如果金额大于1百万,记录警告
    if (value > 1000000) {
      this.log(LogLevel.WARNING, `${symbol}金额异常大: ${formatted}`, null, {
        amount: amount.toString(),
        decimals,
        value,
        formatted,
      });
    }
  }

  /**
   * 获取预计兑换金额
   * @param params 兑换参数
   * @returns 预计兑换结果
   */
  public async getAmountOut(params: SwapParams): Promise<SwapEstimation> {
    try {
      // 首先判断是否是 Four.meme 路由
      if (this.isUsingFourMeme()) {
        this.log(LogLevel.INFO, "使用 Four.meme 计算兑换金额", null, params);
        const router = new ethers.Contract(
          this.config.dex.router.address,
          FOUR_MEME_ABI,
          this.provider
        );

        // 获取输入金额
        let decimals = 18;
        if (params.tokenInDecimals) {
          decimals = params.tokenInDecimals;
        }
        const amountIn =
          typeof params.amountIn === "string"
            ? ethers.utils.parseUnits(params.amountIn, decimals)
            : params.amountIn;

        // 判断是买入还是卖出操作
        const isBuyOperation = params.tokenOut.toLowerCase() !== NATIVE_TOKEN.toLowerCase() && 
                              params.tokenOut.toLowerCase() !== WBNB.toLowerCase();
        
        // 判断是卖出操作（代币换成BNB）
        const isSellOperation = (params.tokenIn.toLowerCase() !== NATIVE_TOKEN.toLowerCase() && 
                                params.tokenIn.toLowerCase() !== WBNB.toLowerCase()) && 
                                (params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase() || 
                                params.tokenOut.toLowerCase() === WBNB.toLowerCase());

        try {
          if (isBuyOperation) {
            // 买入操作 - 使用代币信息计算买入金额
            this.log(LogLevel.INFO, "FourMeme: 执行买入操作", null, { tokenOut: params.tokenOut });
            
            // 获取代币信息
            const tokenInfo = await router._tokenInfos(params.tokenOut);
            
            // 计算预期输出金额
            const amountOut = await router.calcBuyAmount(tokenInfo, amountIn);
            
            return {
              amountOut,
              priceImpact: 0, // Four.meme 不计算价格影响
            };
          } else if (isSellOperation) {
            // 卖出操作 - 使用代币信息计算卖出金额
            this.log(LogLevel.INFO, "FourMeme: 执行卖出操作", null, { tokenIn: params.tokenIn });
            
            // 获取代币信息
            const tokenInfo = await router._tokenInfos(params.tokenIn);
            
            try {
              // 尝试使用calcSellAmount计算卖出金额（如果合约支持此方法）
              const amountOut = await router.calcSellAmount(tokenInfo, amountIn);
              
              return {
                amountOut,
                priceImpact: 0,
              };
            } catch (sellError: unknown) {
              // 如果calcSellAmount方法不存在或失败，尝试获取配对信息手动计算
              this.log(LogLevel.WARNING, "FourMeme: calcSellAmount方法失败，尝试其他方法", null, { error: sellError instanceof Error ? sellError.message : String(sellError) });
              
              // 尝试从tokenInfo获取流动性信息
              try {
                // 获取代币和BNB的价格关系
                const bnbBalance = await this.provider.getBalance(this.config.dex.router.address);
                const tokenBalance = await new ethers.Contract(
                  params.tokenIn,
                  ["function balanceOf(address) view returns (uint256)"],
                  this.provider
                ).balanceOf(this.config.dex.router.address);
                
                if (tokenBalance.isZero()) {
                  throw new Error("FourMeme: 该代币暂不支持卖出操作，池中代币余额为零");
                }
                
                // 简单估算卖出金额（这是一个非常粗略的估计）
                const estimatedOut = amountIn.mul(bnbBalance).div(tokenBalance);
                
                this.log(LogLevel.INFO, "FourMeme: 手动估算卖出金额", null, { 
                  bnbBalance: ethers.utils.formatEther(bnbBalance),
                  tokenBalance: tokenBalance.toString(),
                  estimatedOut: ethers.utils.formatEther(estimatedOut)
                });
                
                return {
                  amountOut: estimatedOut,
                  priceImpact: 0,
                };
              } catch (poolError: unknown) {
                throw new Error(`FourMeme: 该代币可能不支持卖出操作，或流动性不足: ${poolError instanceof Error ? poolError.message : String(poolError)}`);
              }
            }
          } else {
            // 其他交易情况（代币到代币等）
            throw new Error("FourMeme: 不支持的交易对类型");
          }
        } catch (fourMemeError: unknown) {
          // 特别处理panic code 18错误（除零错误）
          const errorMsg = fourMemeError instanceof Error ? fourMemeError.message : String(fourMemeError);
          if (errorMsg.includes("code 18") || errorMsg.includes("panic code 18")) {
            if (isSellOperation) {
              throw new Error("FourMeme: 该代币目前不支持卖出操作，可能是流动性不足或合约限制");
            } else {
              throw new Error("FourMeme: 计算交易金额失败，可能是流动性不足（除零错误）");
            }
          }
          // 重新抛出其他错误
          throw fourMemeError;
        }
      }

      // 获取代币信息，处理原生代币情况
      const [tokenInInfo, tokenOutInfo] = await Promise.all([
        params.tokenIn.toLowerCase() === NATIVE_TOKEN.toLowerCase()
          ? {
              name: "Binance Coin",
              symbol: "BNB",
              decimals: 18,
              address: NATIVE_TOKEN,
            }
          : this.getTokenInfo(params.tokenIn),
        params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase()
          ? {
              name: "Binance Coin",
              symbol: "BNB",
              decimals: 18,
              address: NATIVE_TOKEN,
            }
          : this.getTokenInfo(params.tokenOut),
      ]);
      // 获取实际交易对地址（使用WBNB替代BNB）
      const actualTokenIn =
        params.tokenIn.toLowerCase() === NATIVE_TOKEN.toLowerCase()
          ? WBNB
          : params.tokenIn;
      const actualTokenOut =
        params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase()
          ? WBNB
          : params.tokenOut;

      // 检查流动性池是否存在
      const pairAddress = await this.factory.getPair(
        actualTokenIn,
        actualTokenOut
      );
      if (pairAddress === ethers.constants.AddressZero) {
        throw new Error("流动性池不存在");
      }

      // 获取流动性池合约
      const pair = new ethers.Contract(
        pairAddress,
        [
          "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
          "function token0() external view returns (address)",
          "function token1() external view returns (address)",
        ],
        this.provider
      );

      // 获取流动性池储备
      const [reserve0, reserve1] = await pair.getReserves();
      const token0 = await pair.token0();

      // 确定正确的储备顺序
      const [reserveIn, reserveOut] =
        token0.toLowerCase() === actualTokenIn.toLowerCase() // 使用 actualTokenIn
          ? [reserve0, reserve1]
          : [reserve1, reserve0];

      // 检查流动性是否足够
      if (reserveIn.isZero() || reserveOut.isZero()) {
        throw new Error("流动性池中没有足够的代币");
      }

      // 验证并解析输入金额
      const amountIn = ethers.utils.parseUnits(
        params.amountIn,
        tokenInInfo.decimals
      );
      this.validateTokenAmount(
        amountIn,
        tokenInInfo.decimals,
        tokenInInfo.symbol
      );

      // 记录流动性信息
      this.log(LogLevel.INFO, "流动性池信息", null, {
        pairAddress,
        tokenIn: {
          symbol: tokenInInfo.symbol,
          reserve: ethers.utils.formatUnits(reserveIn, tokenInInfo.decimals),
        },
        tokenOut: {
          symbol: tokenOutInfo.symbol,
          reserve: ethers.utils.formatUnits(reserveOut, tokenOutInfo.decimals),
        },
      });

      // 记录原始金额
      this.log(LogLevel.INFO, "原始金额", null, {
        tokenIn: {
          symbol: tokenInInfo.symbol,
          decimals: tokenInInfo.decimals,
          amount: amountIn.toString(),
          formatted: ethers.utils.formatUnits(amountIn, tokenInInfo.decimals),
        },
        tokenOut: {
          symbol: tokenOutInfo.symbol,
          decimals: tokenOutInfo.decimals,
        },
      });

      // 获取预计输出金额
      const amounts = await this.router.getAmountsOut(amountIn, [
        actualTokenIn,
        actualTokenOut,
      ]);

      if (!amounts || amounts.length !== 2) {
        throw new Error("无效的路由器响应");
      }

      const amountOut = amounts[1];

      // 验证输出金额
      if (amountOut.isZero()) {
        throw new Error("输出金额为0,可能是流动性不足");
      }

      // 计算价格影响
      const priceImpact = this.calculatePriceImpact(
        amountIn,
        amountOut,
        reserveIn,
        reserveOut,
        tokenInInfo.decimals,
        tokenOutInfo.decimals
      );

      // 记录预估结果
      this.log(LogLevel.INFO, "兑换预估结果", null, {
        amountIn: ethers.utils.formatUnits(amountIn, tokenInInfo.decimals),
        amountOut: ethers.utils.formatUnits(amountOut, tokenOutInfo.decimals),
        priceImpact,
      });

      return {
        amountOut,
        priceImpact,
      };
    } catch (error: any) {
      // 优化错误消息
      let message = error.message;
      if (message.includes("Pancake: K")) {
        message = "代币兑换失败: 流动性不足或价格影响过大";
      }

      this.log(LogLevel.ERROR, `获取预计兑换金额失败: ${message}`, null, {
        error,
      });
      throw new Error(message);
    }
  }

  /**
   * 计算价格影响
   * @param amountIn 输入金额
   * @param amountOut 输出金额
   * @param reserveIn 输入代币储备
   * @param reserveOut 输出代币储备
   * @param decimalsIn 输入代币精度
   * @param decimalsOut 输出代币精度
   * @returns 价格影响百分比
   * @protected
   */
  protected calculatePriceImpact(
    amountIn: BigNumber,
    amountOut: BigNumber,
    reserveIn: BigNumber,
    reserveOut: BigNumber,
    decimalsIn: number,
    decimalsOut: number
  ): number {
    try {
      // 如果输出金额为0,返回100%的价格影响
      if (amountOut.isZero()) {
        return 100;
      }

      // 标准化所有金额到18位精度进行计算
      const scale = (decimals: number) => BigNumber.from(10).pow(18 - decimals);

      const normalizedAmountIn = amountIn.mul(scale(decimalsIn));
      const normalizedAmountOut = amountOut.mul(scale(decimalsOut));
      const normalizedReserveIn = reserveIn.mul(scale(decimalsIn));
      const normalizedReserveOut = reserveOut.mul(scale(decimalsOut));

      // 计算理想输出金额(使用恒定乘积公式)
      const idealAmountOut = normalizedAmountIn
        .mul(normalizedReserveOut)
        .div(normalizedReserveIn);

      // 计算价格影响
      const impact = idealAmountOut
        .sub(normalizedAmountOut)
        .mul(ethers.constants.WeiPerEther)
        .div(idealAmountOut);

      const impactPercent = parseFloat(ethers.utils.formatUnits(impact, 16));
      return Math.max(0, Math.min(100, impactPercent));
    } catch (error: any) {
      this.log(LogLevel.ERROR, `计算价格影响失败: ${error.message}`, null, {
        error,
      });
      return 100; // 发生错误时返回最大价格影响
    }
  }

  /**
   * 批量执行代币兑换
   * @param params 兑换参数数组，每个元素代表一个交易任务
   * @returns 兑换结果列表
   */
  public async batchSwap(params: SwapParams[]): Promise<
    Array<{
      walletIndex: number;
      success: boolean;
      result?: SwapResult;
      error?: string;
    }>
  > {
    const results: Array<{
      walletIndex: number;
      success: boolean;
      result?: SwapResult;
      error?: string;
    }> = [];

    // 日志记录
    this.log(LogLevel.INFO, `开始执行批量兑换，共 ${params.length} 个任务`);

    // 确保参数是数组
    if (!Array.isArray(params)) {
      throw new Error("批量兑换参数必须是数组");
    }

    // 直接遍历交易参数数组，每个参数代表一个交易任务
    for (let i = 0; i < params.length; i++) {
      const param = params[i];
      // 确定当前任务使用的钱包索引
      const walletIndex = i % this.wallets.length; // 使用任务索引对钱包数量取模
      
      try {
        const wallet = this.wallets[walletIndex];
        if (!wallet) {
          throw new Error(`钱包索引 ${walletIndex} 不存在`);
        }
        
        this.log(LogLevel.INFO, `任务 ${i+1}/${params.length}：使用钱包 ${walletIndex} 执行兑换`, walletIndex);

        // 创建参数副本，防止修改原参数对象
        const paramCopy = {...param};
        
        // 检查代币余额
        const balance = await this.checkTokenBalance(
          paramCopy.tokenIn,
          wallet.address
        );
        const formattedBalance = ethers.utils.formatUnits(
          balance,
          paramCopy.tokenInDecimals
        );
        this.log(
          LogLevel.INFO,
          `钱包 ${walletIndex} 当前余额: ${formattedBalance}`,
          walletIndex
        );

        const amountIn =
          typeof paramCopy.amountIn === "string"
            ? ethers.utils.parseUnits(paramCopy.amountIn, paramCopy.tokenInDecimals)
            : paramCopy.amountIn;

        if (balance.lt(amountIn)) {
          const error = `钱包 ${walletIndex} 余额不足`;
          this.log(LogLevel.ERROR, error, walletIndex);
          throw new Error(error);
        }

        // 检查授权
        const allowance = await this.getAllowance(
          paramCopy.tokenIn,
          wallet.address,
          this.config.dex.router.address
        );

        if (allowance.lt(amountIn)) {
          this.log(
            LogLevel.INFO,
            `钱包 ${walletIndex} 需要授权`,
            walletIndex
          );
          const approveTx = await this.approve(
            paramCopy.tokenIn,
            this.config.dex.router.address,
            ethers.constants.MaxUint256,
            wallet.address
          );
          await approveTx.wait();
          this.log(
            LogLevel.SUCCESS,
            `钱包 ${walletIndex} 授权成功`,
            walletIndex
          );
        }

        // 执行兑换
        this.log(
          LogLevel.INFO,
          `钱包 ${walletIndex} 开始执行兑换`,
          walletIndex
        );
        const result = await this.swap(paramCopy, wallet.address);

        if (result.success) {
          this.log(
            LogLevel.SUCCESS,
            `钱包 ${walletIndex} 兑换成功`,
            walletIndex,
            {
              txHash: result.txHash,
              amountOut: result.amountOut?.toString(),
              gasUsed: result.gasUsed?.toString(),
            }
          );
        } else {
          throw new Error(result.error || "未知错误");
        }

        results.push({
          walletIndex,
          success: true,
          result,
        });
      } catch (error: any) {
        this.log(
          LogLevel.ERROR,
          `任务 ${i+1}/${params.length} 执行失败: ${error.message}`,
          walletIndex,
          { error }
        );
        results.push({
          walletIndex,
          success: false,
          error: error.message,
        });
      }
    }

    // 总结日志
    const successCount = results.filter(r => r.success).length;
    this.log(
      LogLevel.INFO, 
      `批量兑换完成: ${successCount}/${params.length} 成功`
    );

    return results;
  }

  /**
   * 批量执行代币兑换 (并行优化版)
   * @param params 兑换参数数组，每个元素代表一个交易任务
   * @returns 兑换结果列表
   */
  public async batchSwapParallel(
    params: SwapParams[]
  ): Promise<
    Array<{
      walletIndex: number;
      success: boolean;
      result?: SwapResult;
      error?: string;
    }>
  > {
    const results: Array<{
      walletIndex: number;
      success: boolean;
      result?: SwapResult;
      error?: string;
      taskIndex?: number;
    }> = [];


    const concurrency = this.maxThreads;

    // 日志记录
    this.log(LogLevel.INFO, `开始执行并行批量兑换，共 ${params.length} 个任务, 并发数 ${concurrency}`);

    // 确保参数是数组
    if (!Array.isArray(params)) {
      throw new Error("批量兑换参数必须是数组");
    }




    // 将任务分组
    const taskGroups: SwapParams[][] = [];
    for (let i = 0; i < params.length; i += concurrency) {
      taskGroups.push(params.slice(i, i + concurrency));
    }
    
    // 按组并行处理任务
    for (let groupIndex = 0; groupIndex < taskGroups.length; groupIndex++) {
      const group = taskGroups[groupIndex];
      this.log(LogLevel.INFO, `处理任务组 ${groupIndex + 1}/${taskGroups.length}，包含 ${group.length} 个任务`);
      
      // 并行执行当前组内的所有任务
      const groupPromises = group.map((param, index) => {
        const taskIndex = groupIndex * concurrency + index;
        // 如果SwapParams中有walletIndex则使用，否则计算一个默认的钱包索引
        const actualWalletIndex = param.walletIndex !== undefined ? param.walletIndex : taskIndex % this.wallets.length;
        return this.executeSwapTask(param, actualWalletIndex, taskIndex);
      });
      
      // 等待当前组的所有任务完成
      const groupResults = await Promise.all(groupPromises);
      results.push(...groupResults);
    }
    
    // 总结日志
    const successCount = results.filter(r => r.success).length;
    this.log(
      LogLevel.INFO, 
      `并行批量兑换完成: ${successCount}/${params.length} 成功`
    );

    // 按原始任务顺序排序结果
    results.sort((a, b) => (a.taskIndex || 0) - (b.taskIndex || 0));
    
    // 移除临时属性
    return results.map(({ taskIndex, ...rest }) => rest);
  }
  
  /**
   * 执行单个交换任务
   * @param param 交换参数
   * @param walletIndex 钱包索引
   * @param taskIndex 任务索引（可选）
   * @returns 交换结果
   * @private
   */
  private async executeSwapTask(
    param: SwapParams,
    walletIndex: number,
    taskIndex?: number
  ): Promise<{
    walletIndex: number;
    success: boolean;
    result?: SwapResult;
    error?: string;
    taskIndex?: number;
  }> {
    try {
      const wallet = this.wallets[walletIndex];
      if (!wallet) {
        throw new Error(`钱包索引 ${walletIndex} 不存在`);
      }
      
      this.log(LogLevel.INFO, `任务 ${taskIndex !== undefined ? taskIndex + 1 : '未知'}：使用钱包 ${walletIndex} 执行兑换`, walletIndex);

      // 创建参数副本，防止修改原参数对象
      const paramCopy = {...param};
      
      // 检查代币余额
      const balance = await this.checkTokenBalance(
        paramCopy.tokenIn,
        wallet.address
      );
      const formattedBalance = ethers.utils.formatUnits(
        balance,
        paramCopy.tokenInDecimals
      );
      this.log(
        LogLevel.INFO,
        `钱包 ${walletIndex} 当前余额: ${formattedBalance}`,
        walletIndex
      );

      const amountIn =
        typeof paramCopy.amountIn === "string"
          ? ethers.utils.parseUnits(paramCopy.amountIn, paramCopy.tokenInDecimals)
          : paramCopy.amountIn;

      if (balance.lt(amountIn)) {
        const error = `钱包 ${walletIndex} 余额不足`;
        this.log(LogLevel.ERROR, error, walletIndex);
        throw new Error(error);
      }

      // 检查授权
      const allowance = await this.getAllowance(
        paramCopy.tokenIn,
        wallet.address,
        this.config.dex.router.address
      );

      if (allowance.lt(amountIn)) {
        this.log(
          LogLevel.INFO,
          `钱包 ${walletIndex} 需要授权`,
          walletIndex
        );
        const approveTx = await this.approve(
          paramCopy.tokenIn,
          this.config.dex.router.address,
          ethers.constants.MaxUint256,
          wallet.address
        );
        await approveTx.wait();
        this.log(
          LogLevel.SUCCESS,
          `钱包 ${walletIndex} 授权成功`,
          walletIndex
        );
      }

      // 执行兑换
      this.log(
        LogLevel.INFO,
        `钱包 ${walletIndex} 开始执行兑换`,
        walletIndex
      );
      const result = await this.swap(paramCopy, wallet.address);

      if (result.success) {
        this.log(
          LogLevel.SUCCESS,
          `钱包 ${walletIndex} 兑换成功`,
          walletIndex,
          {
            txHash: result.txHash,
            amountOut: result.amountOut?.toString(),
            gasUsed: result.gasUsed?.toString(),
          }
        );
      } else {
        throw new Error(result.error || "未知错误");
      }

      return {
        walletIndex,
        success: true,
        result,
        taskIndex
      };
    } catch (error: any) {
      this.log(
        LogLevel.ERROR,
        `任务 ${taskIndex !== undefined ? taskIndex + 1 : '未知'} 执行失败: ${error.message}`,
        walletIndex,
        { error }
      );
      return {
        walletIndex,
        success: false,
        error: error.message,
        taskIndex
      };
    }
  }

  /**
   * 获取代币余额
   * @param tokenAddress 代币合约地址
   * @param decimals 代币精度
   * @param walletIndex 钱包索引
   * @returns 余额字符串
   * @protected
   */
  protected async getTokenBalance(
    tokenAddress: string,
    decimals: number,
    walletIndex: number
  ): Promise<string> {
    try {
      const wallet = this.wallets[walletIndex];
      // 如果是原生代币，直接返回余额
      if (tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return ethers.utils.formatEther(
          await this.getNativeBalance(wallet.address)
        );
      }
      const tokenContract = this.getTokenContract(tokenAddress);
      const balance = await tokenContract.balanceOf(wallet.address);
      return ethers.utils.formatUnits(balance, decimals);
    } catch (error: any) {
      this.log(
        LogLevel.ERROR,
        `获取代币余额失败: ${error.message}`,
        walletIndex,
        {
          error,
          callParams: {
            tokenAddress,
            decimals,
            walletIndex,
          },
        }
      );
      throw error;
    }
  }

  /**
   * 处理 Four.meme 交易
   * @param params 兑换参数
   * @param wallet 钱包实例
   * @param minAmountOut 最小获得的代币数量
   * @returns 交易结果
   * @protected
   */
  protected async handleFourMemeSwap(
    params: SwapParams,
    wallet: ethers.Wallet,
    minAmountOut: BigNumber
  ): Promise<ethers.ContractTransaction> {
    try {
      // 1. 输入验证
      if (!params || !wallet || !minAmountOut) {
        throw new Error("Invalid parameters for Four.meme swap");
      }
  
      this.log(LogLevel.INFO, "使用Four.meme交易", null, {
        tokenIn: params.tokenIn,
        tokenOut: params.tokenOut,
        amountIn: params.amountIn.toString(),
        minOut: minAmountOut.toString()
      });
  
      // 2. Gas配置
      const options = await this.buildGasOptions(params);
  
      // 3. 金额处理
      const amountIn = typeof params.amountIn === "string"
        ? ethers.utils.parseUnits(params.amountIn, params.tokenInDecimals)
        : params.amountIn;
  
      // 5. 创建路由合约
      const fourMemeRouter = new ethers.Contract(
        this.config.dex.router.address,
        FOUR_MEME_ABI,
        wallet.provider
      );
  
      // 6. 获取代币信息
      const tokenAddress = params.tokenIn.toLowerCase() === NATIVE_TOKEN.toLowerCase() 
        ? params.tokenOut 
        : params.tokenIn;
      const tokenInfo = await fourMemeRouter._tokenInfos(tokenAddress);
  
      // 7. 检查代币授权(针对sellToken)
      if (params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        const walletIndex = this.wallets.findIndex(w => w.address === wallet.address);
        if (walletIndex === -1) {
          throw new Error("未找到对应的钱包");
        }
        await this.checkAndApproveToken(
          params.tokenIn,
          amountIn,
          params.tokenInDecimals,
          walletIndex,
          params
        );
      }
  
      // 8. 执行交易
      if (params.tokenIn.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        // BNB -> Token
        this.log(LogLevel.INFO, "使用Four.meme buyToken方法", null, {
          token: params.tokenOut,
          amountIn: amountIn.toString(),
          minOut: minAmountOut.toString(),
          maxBuyAmount: params.maxBuyAmount?.toString()
        });
  
        const data = fourMemeRouter.interface.encodeFunctionData(
          "buyTokenAMAP(address,uint256,uint256)",
          params.maxBuyAmount && !params.maxBuyAmount.isZero()
            ? [params.tokenOut, params.maxBuyAmount, amountIn]
            : [params.tokenOut, amountIn, minAmountOut]
        );
  
        return await wallet.sendTransaction({
          to: this.config.dex.router.address,
          data,
          value: amountIn,
          ...options
        });
      } else if (params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        // Token -> BNB
        this.log(LogLevel.INFO, "使用Four.meme sellToken方法", null, {
          token: params.tokenIn,
          amountIn: amountIn.toString(),
          minOut: minAmountOut.toString()
        });
  
        const data = fourMemeRouter.interface.encodeFunctionData(
          "sellToken(address,uint256,uint256)",
          [params.tokenIn, amountIn, minAmountOut]
        );
  
        return await wallet.sendTransaction({
          to: this.config.dex.router.address,
          data,
          ...options
        });
      }
  
      throw new Error("Unsupported token pair for Four.meme swap");
    } catch (error: any) {
      this.log(LogLevel.ERROR, `Four.meme交易失败: ${error.message}`, null, { error });
      throw error;
    }
  }

  /**
   * 执行代币兑换
   * @param params 兑换参数
   * @param wallet 钱包实例
   * @param minAmountOut 最小获得的代币数量
   * @returns 交易结果
   * @protected
   */
  protected async executeSwap(
    params: SwapParams,
    wallet: ethers.Wallet,
    minAmountOut: BigNumber
  ): Promise<ethers.ContractTransaction> {
    try {
      // 检查是否是Four.meme路由器
      if (this.isUsingFourMeme()) {
        return this.handleFourMemeSwap(params, wallet, minAmountOut);
      }

      const options = await this.buildGasOptions(params);
      const router = this.router.connect(wallet);
      const amountIn =
        typeof params.amountIn === "string"
          ? ethers.utils.parseUnits(params.amountIn, params.tokenInDecimals)
          : params.amountIn;

      // 计算截止时间
      const deadline =
        Math.floor(Date.now() / 1000) + (params.deadline || 20) * 60;

      // 对于其他DEX,使用标准的swap方法
      this.log(LogLevel.INFO, "使用标准swap方法", null, {
        amountIn: amountIn.toString(),
        minOut: minAmountOut.toString(),
        path: [params.tokenIn, params.tokenOut],
      });

      if (params.tokenIn.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return await router.swapExactETHForTokensSupportingFeeOnTransferTokens(
          minAmountOut,
          [WBNB, params.tokenOut],
          wallet.address,
          deadline,
          { value: amountIn, ...options }
        );
      } else if (params.tokenOut.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        return await router.swapExactTokensForETHSupportingFeeOnTransferTokens(
          amountIn,
          minAmountOut,
          [params.tokenIn, WBNB],
          wallet.address,
          deadline,
          { ...options }
        );
      } else {
        return await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
          amountIn,
          minAmountOut,
          [params.tokenIn, params.tokenOut],
          wallet.address,
          deadline,
          { ...options }
        );
      }
    } catch (error: any) {
      this.log(LogLevel.ERROR, `执行swap失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 检查指定钱包地址的代币余额
   * @param tokenAddress 代币合约地址
   * @param walletAddress 钱包地址
   * @returns 代币余额
   */
  public async checkTokenBalance(
    tokenAddress: string,
    walletAddress: string
  ): Promise<BigNumber> {
    try {
      this.log(LogLevel.INFO, "开始检查代币余额", null, {
        tokenAddress,
        walletAddress,
      });

      // 如果是原生代币，直接返回余额
      if (tokenAddress.toLowerCase() === NATIVE_TOKEN.toLowerCase()) {
        const wallet = this.wallets.find((w) => w.address === walletAddress);
        if (!wallet) {
          throw new Error("未找到对应的钱包实例");
        }
        const balance = await this.getNativeBalance(walletAddress);
        return balance;
      }

      // 验证地址格式
      if (!ethers.utils.isAddress(tokenAddress)) {
        throw new Error("无效的代币合约地址");
      }
      if (!ethers.utils.isAddress(walletAddress)) {
        throw new Error("无效的钱包地址");
      }

      // 获取代币信息
      const tokenInfo = await this.getTokenInfo(tokenAddress);

      // 获取代币合约
      const tokenContract = this.getTokenContract(tokenAddress);

      // 查询余额
      const balance = await tokenContract.balanceOf(walletAddress);

      this.log(LogLevel.SUCCESS, "获取代币余额成功", null, {
        tokenAddress,
        walletAddress,
        balance: ethers.utils.formatUnits(balance, tokenInfo.decimals),
      });

      return balance;
    } catch (error: any) {
      this.log(LogLevel.ERROR, `获取代币余额失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 获取指定地址的原生代币余额
   * @param address 钱包地址
   * @returns 原生代币余额
   */
  public async getNativeBalance(address: string): Promise<BigNumber> {
    try {
      this.log(LogLevel.INFO, "开始获取原生代币余额", null, { address });

      if (!ethers.utils.isAddress(address)) {
        throw new Error("无效的钱包地址");
      }

      const balance = await this.provider.getBalance(address);

      this.log(LogLevel.SUCCESS, "获取原生代币余额成功", null, {
        address,
        balance: ethers.utils.formatEther(balance),
      });

      return balance;
    } catch (error: any) {
      this.log(LogLevel.ERROR, `获取原生代币余额失败: ${error.message}`, null, {
        error,
      });
      throw error;
    }
  }

  /**
   * 获取代币余额和信息
   * @param tokenAddress 代币合约地址
   * @param walletAddress 钱包地址
   * @returns Promise<{balance: BigNumber, info: TokenInfo}>
   */
  public async getTokenBalanceAndInfo(
    tokenAddress: string,
    walletAddress: string
  ): Promise<{ balance: BigNumber; info: TokenInfo }> {
    try {
      const [balance, info] = await Promise.all([
        this.checkTokenBalance(tokenAddress, walletAddress),
        this.getTokenInfo(tokenAddress),
      ]);

      this.log(LogLevel.INFO, `获取代币余额和信息`, null, {
        token: tokenAddress,
        wallet: walletAddress,
        balance: balance.toString(),
        symbol: info.symbol,
      });

      return { balance, info };
    } catch (error: any) {
      this.log(
        LogLevel.ERROR,
        `获取代币余额和信息失败: ${error.message}`,
        null,
        {
          token: tokenAddress,
          wallet: walletAddress,
          error: error.message,
        }
      );
      throw error;
    }
  }

  protected async buildGasOptions(params: SwapParams) {
    const isFourMeme =
      this.isFourMemeToken(params.tokenIn) ||
      this.isFourMemeToken(params.tokenOut);

    const options: any = {
      gasLimit: params.gasLimit
        ? ethers.utils.parseUnits(params.gasLimit, "wei")
        : isFourMeme
        ? 1000000
        : 300000, // Four.meme 需要更高的 gas limit
    };

    if (params.gasPrice) {
      let gasPrice = params.gasPrice;
      if (params.gasPriceBoost) {
        gasPrice = (Number(gasPrice) * (1 + params.gasPriceBoost)).toString();
      }
      options.gasPrice = ethers.utils.parseUnits(gasPrice, "gwei");
    }

    return options;
  }

  /**
   * 执行代币兑换
   * @param params 兑换参数
   * @param walletAddress 钱包地址
   * @returns 交易结果
   */
  public async swap(
    params: SwapParams,
    walletAddress: string
  ): Promise<SwapResult> {
    try {
      this.log(LogLevel.INFO, "开始执行代币兑换", null, {
        params,
        walletAddress,
      });

      if (!ethers.utils.isAddress(walletAddress)) {
        throw new Error("无效的钱包地址");
      }

      // 查找钱包
      const walletIndex = this.wallets.findIndex(
        (w) => w.address.toLowerCase() === walletAddress.toLowerCase()
      );
      if (walletIndex === -1) {
        throw new Error("找不到对应的钱包");
      }

      const wallet = this.wallets[walletIndex];
      // 获取预计兑换金额
      const estimation = await this.getAmountOut(params);

      // 计算最小获得的代币数量（考虑滑点）
      const minAmountOut = this.isUsingFourMeme()
        ? BigNumber.from(0) // Four.meme 不需要 minAmountOut
        : estimation.amountOut
            .mul(
              ethers.BigNumber.from(10000 - Math.floor(params.slippage * 100))
            )
            .div(10000);

      // 执行兑换
      const tx = await this.executeSwap(params, wallet, minAmountOut);
      const receipt = await tx.wait();

      return {
        success: true,
        txHash: receipt.transactionHash,
        amountOut: estimation.amountOut,
        gasUsed: receipt.gasUsed,
        effectiveGasPrice: receipt.effectiveGasPrice,
      };
    } catch (error: any) {
      this.log(LogLevel.ERROR, `代币兑换失败: ${error.message}`, null, {
        error,
      });
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * 更新服务配置
   * @param configUpdate 要更新的配置对象
   */
  public updateConfig(configUpdate: Partial<DexServiceConfig>): void {
    // 更新配置
    if (configUpdate.maxThreads !== undefined) {
      this.maxThreads = configUpdate.maxThreads;
      this.config.maxThreads = configUpdate.maxThreads;
      this.log(
        LogLevel.INFO,
        `已更新并行执行线程数: ${configUpdate.maxThreads}`,
        null
      );
    }
    
    // 根据需要添加其他配置更新
  }
}
